import { PalletsListSubItem } from "./pallets-list-sub-item";
import { Pallet } from "../../../model/pallet";
import { action } from "mobx";
import { orderBy } from "lodash";
import { domain, label } from "../../../../common/dto/dto-annotation";
import { weight } from "../../../../domain/weight";
import { quantity } from "../../../../domain/quantity";
import { weightKgs } from "../../../../domain/weight-kgs";
import moment from "moment";
import { localDate } from "../../../../domain/local-date";
import { PalletType } from "../../../constants/constants";
import { DangerousGoods, lithiumUnCodeList } from "../../../../constants/constants";

export class PalletsListItem {
    private readonly _id: string;
    private readonly _code: string;
    private readonly _carrier: string;
    private readonly _country: string;
    private readonly _parcelCount: number;
    private readonly _weight: number;
    private readonly _maxParcelWeight: number;
    private readonly _maxWeight: number;
    private readonly _isLabelPrinted: boolean;
    private _details: PalletsListSubItem[];
    private readonly _pickingDate: moment.Moment | null;
    private readonly _type: PalletType;
    private readonly _isVirtualPallet: boolean;
    private readonly _dangerousGoods: DangerousGoods;
    private readonly _unCodes: string[];
    private readonly _creationComment: string;
    private readonly _closingComment: string;
    private readonly _shippingComment: string;
    private readonly _isClosed: boolean;
    private readonly _isShippedAutomatically: boolean;

    public constructor(pallet: Pallet) {
        this._id = pallet.id.toString();
        this._code = pallet.code;
        this._carrier = pallet.carrier;
        this._country = pallet.countryGroup;
        this._parcelCount = pallet.parcelsCount;
        this._weight = pallet.parcelsWeight;
        this._maxParcelWeight = pallet.maxParcelWeight;
        this._maxWeight = pallet.maxWeight;
        this._isLabelPrinted = pallet.isLabelPrinted;
        this._details = pallet.parcels.map(p => new PalletsListSubItem(p));
        this._pickingDate = pallet.pickingDate;
        this._type = pallet.type;
        this._isVirtualPallet = pallet.isVirtualPallet;
        this._dangerousGoods = pallet.parcels.map(p => p.dangerousGoods)
            .reduce(this.reduceParcelDangerousGoods, DangerousGoods.None);
        this._unCodes = pallet.parcels.flatMap(p => p.details.map(pd => pd.unCode))
            .reduce(this.reduceParcelUnCodes, []);
        this._creationComment = pallet.creationComment;
        this._closingComment = pallet.closingComment;
        this._shippingComment = pallet.shippingComment;
        this._isClosed = pallet.status !== "New" && pallet.status !== "Open";
        this._isShippedAutomatically = pallet.isShippedAutomatically;
    }

    @label("model.pallet.id")
    public get id(): string {
        return this._id;
    }

    @label("model.pallet.id")
    public get palletId(): string {
        return this._id;
    }

    @label("model.pallet.code")
    public get code(): string {
        return this._code;
    }

    @label("model.pallet.code")
    public get codeNumeric(): number {
        return Number(this._code.replace("DS", ""));
    }

    @label("model.pallet.id")
    public get parcelId(): string {
        return this._id;
    }

    @label("model.pallet.carrier")
    public get carrier(): string {
        return this._carrier;
    }

    @label("model.pallet.country")
    public get country(): string {
        return this._country;
    }

    @label("model.pallet.parcelCount")
    @domain(quantity)
    public get parcelCount(): number {
        return this._parcelCount;
    }

    @label("model.pallet.weight")
    @domain(weightKgs)
    public get weight(): number {
        return this._weight;
    }

    @domain(weightKgs)
    public get maxParcelWeight(): number {
        return this._maxParcelWeight;
    }

    @label("model.pallet.maxWeight")
    @domain(weightKgs)
    public get maxWeight(): number {
        return this._maxWeight;
    }

    @label("model.pallet.isLabelPrinted")
    @domain(weight)
    public get isLabelPrinted(): boolean {
        return this._isLabelPrinted;
    }

    @label("model.pallet.pickingDate")
    @domain(localDate)
    public get pickingDate(): moment.Moment | null {
        return this._pickingDate;
    }

    public get type(): PalletType {
        return this._type;
    }

    public get isVirtualPallet(): boolean {
        return this._isVirtualPallet;
    }

    public get details(): PalletsListSubItem[] {
        return this._details;
    }

    public get dangerousGoods() {
        return this._dangerousGoods;
    }

    public get unCodes() {
        return this._unCodes;
    }

    public getDetailByLocator(locator: string): PalletsListSubItem | undefined {
        return this._details.find(parcel => parcel.parcelId.toString() === locator || parcel.parcelTracker === locator);
    }

    public hasParcelWithLocator(locator: string) {
        return this.getDetailByLocator(locator) !== undefined;
    }

    @action
    public sortDetailsBy(subProperty: keyof PalletsListSubItem, sortOrder: "asc" | "desc") {
        this._details = orderBy(this._details, subProperty, sortOrder);
    }

    private reduceParcelDangerousGoods(accumulator: DangerousGoods, current: DangerousGoods) {
        if (accumulator !== current) {
            return accumulator === DangerousGoods.None ? current : DangerousGoods.Both;
        }

        return accumulator;
    }

    private reduceParcelUnCodes(accumulator: string[], current: string) {
        if (lithiumUnCodeList.includes(current)) {
            if (!accumulator.some(unCode => unCode === current)) {
                accumulator.push(current);
            }
        }

        return accumulator;
    }

    get hasComment() {
        return this._creationComment !== "" || this._closingComment !== "" || this._shippingComment !== "";
    }

    get creationComment(): string {
        return this._creationComment;
    }

    get closingComment(): string {
        return this._closingComment;
    }

    get shippingComment(): string {
        return this._shippingComment;
    }

    get isClosed(): boolean {
        return this._isClosed;
    }

    get isShippedAutomatically() {
        return this._isShippedAutomatically;
    }
}
